import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Search, Plus, Trash2, ChevronDown, ChevronUp, Clock, AlertTriangle,
    CheckCircle2, Circle, Pause, MinusCircle, Briefcase, Mail, Crown, Star,
    Compass, Loader2, Archive, ArchiveRestore,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const TIER_ICON = { explorer: Compass, investor: Star, developer: Crown };
const STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: "new", label: "New" },
    { id: "in_progress", label: "In progress" },
    { id: "blocked", label: "Blocked" },
    { id: "completed", label: "Completed" },
];

const STEP_STATUSES = [
    { id: "pending", label: "Pending", icon: Circle, color: "text-muted-foreground" },
    { id: "in_progress", label: "In progress", icon: Loader2, color: "text-coral" },
    { id: "done", label: "Done", icon: CheckCircle2, color: "text-fiji-green" },
    { id: "blocked", label: "Blocked", icon: Pause, color: "text-destructive" },
    { id: "na", label: "N/A", icon: MinusCircle, color: "text-muted-foreground/60" },
];

const STATUS_BADGE = {
    new: { className: "bg-secondary text-foreground border-border", label: "NEW" },
    in_progress: { className: "bg-coral/15 text-coral border-coral/30", label: "IN PROGRESS" },
    blocked: { className: "bg-destructive/15 text-destructive border-destructive/30", label: "BLOCKED" },
    completed: { className: "bg-fiji-green/15 text-fiji-green border-fiji-green/30", label: "COMPLETED" },
};

export default function AdminInvestorPipelinesTab() {
    const [data, setData] = useState({ items: [], stats: null, step_template: [] });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [tierFilter, setTierFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter !== "all") params.set("status", filter);
            if (tierFilter !== "all") params.set("tier_id", tierFilter);
            if (search.trim()) params.set("search", search.trim());
            if (showArchived) params.set("archived", "true");
            const { data: d } = await api.get(`/admin/investor-pipelines?${params}`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load pipelines");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, tierFilter, showArchived]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        reload();
    };

    return (
        <div data-testid="investor-pipelines-tab" className="space-y-6">
            {/* Stats grid */}
            {data.stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pipeline-stats">
                    <StatTile label="Total" value={data.stats.total} accent="ocean" />
                    <StatTile label="New" value={data.stats.by_derived_status?.new || 0} accent="muted" filterId="new" onClick={setFilter} />
                    <StatTile label="In progress" value={data.stats.by_derived_status?.in_progress || 0} accent="coral" filterId="in_progress" onClick={setFilter} />
                    <StatTile label="Blocked" value={data.stats.by_derived_status?.blocked || 0} accent="destructive" filterId="blocked" onClick={setFilter} />
                    <StatTile label="Completed" value={data.stats.by_derived_status?.completed || 0} accent="fiji-green" filterId="completed" onClick={setFilter} />
                </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    {STATUS_FILTERS.map((f) => (
                        <Button
                            key={f.id}
                            variant={filter === f.id ? "default" : "outline"}
                            size="sm"
                            data-testid={`pipeline-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={filter === f.id ? "bg-ocean-green text-white hover:bg-ocean" : ""}
                        >
                            {f.label}
                        </Button>
                    ))}
                    <div className="h-5 w-px bg-border mx-2 hidden md:block" />
                    <Select value={tierFilter} onValueChange={setTierFilter}>
                        <SelectTrigger data-testid="pipeline-tier-filter" className="h-9 w-40 bg-card text-foreground"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All tiers</SelectItem>
                            <SelectItem value="explorer">Explorer</SelectItem>
                            <SelectItem value="investor">Investor</SelectItem>
                            <SelectItem value="developer">Developer</SelectItem>
                        </SelectContent>
                    </Select>
                    <label className="flex items-center gap-2 text-xs font-app text-muted-foreground cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="pipeline-show-archived"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="h-3.5 w-3.5 accent-ocean-green"
                        />
                        Show archived
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <form onSubmit={onSearchSubmit} className="flex gap-2">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                data-testid="pipeline-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search email or name…"
                                className="pl-8 h-9 w-56 bg-card text-foreground"
                            />
                        </div>
                        <Button type="submit" size="sm" variant="outline" className="font-app">Go</Button>
                    </form>
                    <Button
                        size="sm"
                        data-testid="pipeline-create-btn"
                        onClick={() => setCreateOpen(true)}
                        className="bg-coral hover:bg-coral-deep text-white font-app"
                    >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        New pipeline
                    </Button>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center font-app text-muted-foreground py-12" data-testid="pipeline-loading">Loading pipelines…</div>
            ) : data.items.length === 0 ? (
                <Card className="p-10 text-center bg-card" data-testid="pipeline-empty">
                    <Briefcase className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <div className="font-display text-lg font-bold text-foreground mb-1">No pipelines yet</div>
                    <p className="font-app text-sm text-muted-foreground mb-5">
                        Pipelines auto-create when an investor purchases a Concierge tier, or you can create one manually for any contact.
                    </p>
                    <Button onClick={() => setCreateOpen(true)} className="bg-coral text-white">
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Create the first pipeline
                    </Button>
                </Card>
            ) : (
                <div className="space-y-3">
                    {data.items.map((p) => (
                        <PipelineRow
                            key={p.id}
                            pipeline={p}
                            expanded={expandedId === p.id}
                            onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                            onUpdated={reload}
                        />
                    ))}
                </div>
            )}

            {createOpen && (
                <CreatePipelineDialog
                    onClose={() => setCreateOpen(false)}
                    onCreated={() => {
                        setCreateOpen(false);
                        reload();
                    }}
                />
            )}
        </div>
    );
}

function StatTile({ label, value, accent = "ocean", filterId, onClick }) {
    const accents = {
        ocean: "border-ocean/30 text-ocean dark:text-turquoise",
        coral: "border-coral/30 text-coral",
        "fiji-green": "border-fiji-green/30 text-fiji-green",
        destructive: "border-destructive/30 text-destructive",
        muted: "border-border text-foreground",
    };
    const clickable = onClick && filterId;
    return (
        <Card
            className={`p-3 bg-card ${accents[accent]} border ${clickable ? "cursor-pointer hover:shadow-md transition" : ""}`}
            onClick={clickable ? () => onClick(filterId) : undefined}
            data-testid={`pipeline-stat-${filterId || label.toLowerCase()}`}
        >
            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-0.5">{label}</div>
            <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
        </Card>
    );
}

function PipelineRow({ pipeline, expanded, onToggle, onUpdated }) {
    const TierIcon = pipeline.tier_id ? TIER_ICON[pipeline.tier_id] : null;
    const badge = STATUS_BADGE[pipeline.derived_status] || STATUS_BADGE.new;

    return (
        <Card data-testid={`pipeline-row-${pipeline.id}`} className="bg-card overflow-hidden">
            {/* Summary row */}
            <button
                type="button"
                onClick={onToggle}
                data-testid={`pipeline-toggle-${pipeline.id}`}
                className="w-full text-left p-4 hover:bg-secondary/40 dark:hover:bg-card/60 transition flex items-center gap-4"
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge variant="outline" className={`font-app text-[10px] ${badge.className}`}>
                            {badge.label}
                        </Badge>
                        {TierIcon && (
                            <Badge variant="outline" className="font-app text-[10px] border-coral/30 text-coral">
                                <TierIcon className="h-3 w-3 mr-1" /> {pipeline.tier_id}
                            </Badge>
                        )}
                        {pipeline.archived && (
                            <Badge variant="outline" className="font-app text-[10px] text-muted-foreground border-border">
                                <Archive className="h-3 w-3 mr-1" /> archived
                            </Badge>
                        )}
                    </div>
                    <div className="font-display text-base font-bold text-foreground truncate">
                        {pipeline.full_name || pipeline.email}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-app text-muted-foreground mt-0.5">
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{pipeline.email}</span>
                    </div>
                </div>

                <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0 w-44">
                    <div className="text-xs font-app text-muted-foreground">
                        {pipeline.steps_done}/{pipeline.steps_total} steps · {pipeline.progress_pct}%
                    </div>
                    <div className="w-full h-1.5 bg-secondary/60 dark:bg-card/80 rounded overflow-hidden">
                        <div
                            className={`h-full transition-all ${
                                pipeline.derived_status === "blocked"
                                    ? "bg-destructive"
                                    : pipeline.derived_status === "completed"
                                    ? "bg-fiji-green"
                                    : "bg-coral"
                            }`}
                            style={{ width: `${pipeline.progress_pct}%` }}
                        />
                    </div>
                    {pipeline.steps_blocked > 0 && (
                        <div className="text-[10px] font-app text-destructive flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {pipeline.steps_blocked} blocked
                        </div>
                    )}
                </div>

                {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
            </button>

            {expanded && (
                <PipelineDetail pipeline={pipeline} onUpdated={onUpdated} />
            )}
        </Card>
    );
}

function PipelineDetail({ pipeline, onUpdated }) {
    const [pendingStepId, setPendingStepId] = useState(null);

    const updateStep = async (stepId, patch) => {
        setPendingStepId(stepId);
        try {
            await api.patch(`/admin/investor-pipelines/${pipeline.id}/steps/${stepId}`, patch);
            toast.success("Step updated");
            onUpdated();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update step");
        } finally {
            setPendingStepId(null);
        }
    };

    const archive = async () => {
        try {
            await api.patch(`/admin/investor-pipelines/${pipeline.id}`, { archived: !pipeline.archived });
            toast.success(pipeline.archived ? "Restored" : "Archived");
            onUpdated();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
        }
    };

    const remove = async () => {
        if (!confirm(`Permanently delete pipeline for ${pipeline.email}?`)) return;
        try {
            await api.delete(`/admin/investor-pipelines/${pipeline.id}`);
            toast.success("Deleted");
            onUpdated();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
        }
    };

    // Group steps by category for visual grouping
    const grouped = (pipeline.steps || []).reduce((acc, s) => {
        const cat = s.category || "Other";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(s);
        return acc;
    }, {});

    return (
        <div className="border-t border-border bg-secondary/20 dark:bg-card/30 p-4 space-y-5" data-testid={`pipeline-detail-${pipeline.id}`}>
            {pipeline.note && (
                <Card className="p-3 bg-muted/40 dark:bg-card/60 border-border">
                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground font-bold mb-1">Pipeline note</div>
                    <p className="font-app text-sm text-foreground/90 whitespace-pre-wrap">{pipeline.note}</p>
                </Card>
            )}

            {Object.entries(grouped).map(([cat, steps]) => (
                <div key={cat}>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-app text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                        <span className="h-px w-6 bg-border" /> {cat}
                    </div>
                    <div className="space-y-2">
                        {steps.map((s) => (
                            <StepCard
                                key={s.id}
                                step={s}
                                pipelineId={pipeline.id}
                                onUpdate={updateStep}
                                pending={pendingStepId === s.id}
                            />
                        ))}
                    </div>
                </div>
            ))}

            <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                <Button
                    variant="outline"
                    size="sm"
                    data-testid={`pipeline-archive-${pipeline.id}`}
                    onClick={archive}
                    className="font-app"
                >
                    {pipeline.archived ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore</> : <><Archive className="h-3.5 w-3.5 mr-1.5" /> Archive</>}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    data-testid={`pipeline-delete-${pipeline.id}`}
                    onClick={remove}
                    className="font-app text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
                {pipeline.sources?.length > 0 && (
                    <span className="text-xs font-app text-muted-foreground self-center ml-2">
                        Sources: {pipeline.sources.join(" · ")}
                    </span>
                )}
            </div>
        </div>
    );
}

function StepCard({ step, pipelineId, onUpdate, pending }) {
    const [noteOpen, setNoteOpen] = useState(false);
    const [draftNote, setDraftNote] = useState(step.note || "");
    const statusMeta = STEP_STATUSES.find((s) => s.id === step.status) || STEP_STATUSES[0];
    const StatusIcon = statusMeta.icon;

    const onStatusChange = (newStatus) => onUpdate(step.id, { status: newStatus });
    const saveNote = async () => {
        await onUpdate(step.id, { note: draftNote });
        setNoteOpen(false);
    };

    return (
        <Card
            data-testid={`pipeline-step-${pipelineId}-${step.id}`}
            className={`p-3 bg-card ${
                step.status === "blocked"
                    ? "border-destructive/40"
                    : step.status === "done"
                    ? "border-fiji-green/40"
                    : step.status === "in_progress"
                    ? "border-coral/40"
                    : "border-border"
            }`}
        >
            <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${statusMeta.color}`}>
                    {pending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <StatusIcon className={`h-4 w-4 ${step.status === "in_progress" ? "animate-spin" : ""}`} />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-app text-sm font-semibold text-foreground">
                        {step.label}
                    </div>
                    {step.note && (
                        <p className="font-app text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{step.note}</p>
                    )}
                    {step.completed_at && (
                        <div className="flex items-center gap-1 text-[10px] text-fiji-green font-app mt-1">
                            <CheckCircle2 className="h-3 w-3" />
                            {new Date(step.completed_at).toLocaleString()}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <Select value={step.status} onValueChange={onStatusChange}>
                        <SelectTrigger
                            data-testid={`pipeline-step-status-${step.id}`}
                            className="h-8 w-32 text-xs bg-card text-foreground"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STEP_STATUSES.map((st) => (
                                <SelectItem key={st.id} value={st.id}>{st.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setDraftNote(step.note || ""); setNoteOpen(true); }}
                        className="font-app text-xs h-8"
                        data-testid={`pipeline-step-note-btn-${step.id}`}
                    >
                        Note
                    </Button>
                </div>
            </div>

            {noteOpen && (
                <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`pipeline-step-note-edit-${step.id}`}>
                    <Textarea
                        value={draftNote}
                        onChange={(e) => setDraftNote(e.target.value)}
                        rows={3}
                        placeholder="Add a note for this step (visible to admins only)…"
                        className="bg-card text-foreground resize-none text-sm"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setNoteOpen(false)} className="font-app text-xs">Cancel</Button>
                        <Button size="sm" onClick={saveNote} className="bg-ocean-green text-white font-app text-xs">Save note</Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function CreatePipelineDialog({ onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", full_name: "", tier_id: "", note: "" });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email) {
            toast.error("Email required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/investor-pipelines", {
                email: form.email,
                full_name: form.full_name || undefined,
                tier_id: form.tier_id || undefined,
                source: "manual",
                note: form.note || undefined,
            });
            toast.success("Pipeline created");
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not create");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="pipeline-create-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Create investor pipeline</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Tracks a 12-step service checklist per investor. If a pipeline already exists for this email, it will be updated (idempotent).
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-3" data-testid="pipeline-create-form">
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                        <Input data-testid="pipeline-create-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Full name</Label>
                        <Input data-testid="pipeline-create-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-card text-foreground" />
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Concierge tier (optional)</Label>
                        <Select value={form.tier_id} onValueChange={(v) => update("tier_id", v)}>
                            <SelectTrigger data-testid="pipeline-create-tier" className="bg-card text-foreground">
                                <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="explorer">Explorer</SelectItem>
                                <SelectItem value="investor">Investor</SelectItem>
                                <SelectItem value="developer">Developer</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Internal note</Label>
                        <Textarea data-testid="pipeline-create-note" value={form.note} onChange={(e) => update("note", e.target.value)} rows={3} className="bg-card text-foreground resize-none" />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="pipeline-create-submit" className="bg-coral hover:bg-coral-deep text-white">
                            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                            Create
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
